import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { isNative } from '../utils/native';
import { showAdPrivacyOptions } from '../utils/adConsent';
import { useTranslation } from '../contexts/LanguageContext';
import { t as translate } from '../i18n';
import './CookieConsent.css';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const GA4_ID = 'G-JM3M6D2N6Y';
const ADSENSE_CLIENT = 'ca-pub-7453254687360946';

/**
 * Get the current cookie consent state.
 * Returns { necessary: true, advertising: bool } or null if not yet set.
 */
export function getCookieConsent() {
    try {
        const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        // ignore
    }
    return null;
}

/**
 * Check if advertising cookies are consented to.
 */
export function hasAdConsent() {
    const consent = getCookieConsent();
    return consent?.advertising === true;
}

/**
 * Dynamically load GA4 + AdSense scripts.
 * Called only after user consents to advertising cookies.
 */
let scriptsLoaded = false;
function loadTrackingScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;

    // GA4
    const ga4Script = document.createElement('script');
    ga4Script.async = true;
    ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(ga4Script);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA4_ID);

    // AdSense
    const adsenseScript = document.createElement('script');
    adsenseScript.async = true;
    adsenseScript.crossOrigin = 'anonymous';
    adsenseScript.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    document.head.appendChild(adsenseScript);
}

// On page load, if consent already exists, load scripts immediately
if (hasAdConsent()) {
    loadTrackingScripts();
}

// Allow other components to reopen the cookie settings banner
let openCookieSettings = null;

// Catalog key for the menu entry that reopens consent; translate at render time.
export const PRIVACY_SETTINGS_KEY = isNative() ? 'consent.adPrivacyChoices' : 'consent.cookieSettings';

/**
 * Let the user change consent: Google's ad consent form in the apps, the cookie banner on web.
 */
export function reopenCookieSettings() {
    if (isNative()) {
        showAdPrivacyOptions().then((shown) => {
            if (!shown) alert(translate('consent.noAdChoices'));
        });
        return;
    }
    if (openCookieSettings) openCookieSettings();
}

function CookieConsent() {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [advertising, setAdvertising] = useState(() => {
        const consent = getCookieConsent();
        return consent?.advertising === true;
    });

    useEffect(() => {
        // Register the opener so other components can reopen the banner
        openCookieSettings = () => {
            setShowDetails(true);
            setVisible(true);
        };
        return () => { openCookieSettings = null; };
    }, []);

    // Lets the page keep its primary buttons reachable above the banner.
    useEffect(() => {
        document.body.classList.toggle('wl-cookie-open', visible);
        return () => document.body.classList.remove('wl-cookie-open');
    }, [visible]);

    useEffect(() => {
        // Native mobile apps use ATT (iOS) / Google UMP (Android) for consent,
        // not our web cookie banner
        if (isNative()) return;

        const consent = getCookieConsent();
        if (!consent) {
            // Small delay so it doesn't flash on page load
            const timer = setTimeout(() => setVisible(true), 500);
            return () => clearTimeout(timer);
        }
    }, []);

    const saveConsent = useCallback((adConsent) => {
        const consent = {
            necessary: true, // always required
            advertising: adConsent,
            timestamp: new Date().toISOString(),
        };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));

        // Also set a cookie so the server can read it
        const maxAge = 365 * 24 * 60 * 60; // 1 year
        document.cookie = `cookie_consent=${encodeURIComponent(JSON.stringify(consent))}; path=/; max-age=${maxAge}; SameSite=Lax`;

        // Record consent server-side for GDPR audit trail. Use the shared
        // helper so the request carries the X-CSRFToken header and the
        // Capacitor identification header, matching every other API call.
        import('../services/apiBase').then(({ apiRequest }) => {
            apiRequest('/consent/', {
                method: 'POST',
                body: JSON.stringify({ necessary: true, advertising: adConsent }),
            }).catch(() => { /* best-effort */ });
        }).catch(() => { /* module load failure is best-effort */ });

        setVisible(false);

        if (adConsent) {
            // Load GA4 + AdSense now that user consented
            loadTrackingScripts();
            window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));
        }
    }, []);

    const handleAcceptAll = useCallback(() => {
        saveConsent(true);
    }, [saveConsent]);

    const handleAcceptNecessary = useCallback(() => {
        saveConsent(false);
    }, [saveConsent]);

    const handleSavePreferences = useCallback(() => {
        saveConsent(advertising);
    }, [saveConsent, advertising]);

    if (!visible) return null;

    return (
        <div className="cookie-consent-overlay">
            <div className="cookie-consent-banner" role="region" aria-label={t('consent.cookieSettings')}>
                <div className="cookie-consent-content">
                    <h3>{t('consent.cookieSettings')}</h3>
                    <p>{t.rich('consent.intro', { link: <Link to="/privacy" /> })}</p>

                    {showDetails && (
                        <div className="cookie-consent-details">
                            <div className="cookie-category">
                                <div className="cookie-category-header">
                                    <label>
                                        <input type="checkbox" checked disabled />
                                        <strong>{t('consent.necessaryTitle')}</strong>
                                    </label>
                                    <span className="cookie-always-on">{t('consent.alwaysActive')}</span>
                                </div>
                                <p>{t('consent.necessaryDesc')}</p>
                            </div>

                            <div className="cookie-category">
                                <div className="cookie-category-header">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={advertising}
                                            onChange={(e) => setAdvertising(e.target.checked)}
                                        />
                                        <strong>{t('consent.analyticsTitle')}</strong>
                                    </label>
                                </div>
                                <p>{t('consent.analyticsDesc')}</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="cookie-consent-actions">
                    {!showDetails ? (
                        <>
                            <button className="cookie-btn cookie-btn-secondary" onClick={handleAcceptNecessary}>
                                {t('consent.necessaryOnly')}
                            </button>
                            <button className="cookie-btn cookie-btn-outline" onClick={() => setShowDetails(true)}>
                                {t('consent.customize')}
                            </button>
                            <button className="cookie-btn cookie-btn-primary" onClick={handleAcceptAll}>
                                {t('consent.acceptAll')}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="cookie-btn cookie-btn-secondary" onClick={() => setShowDetails(false)}>
                                {t('common.back')}
                            </button>
                            <button className="cookie-btn cookie-btn-primary" onClick={handleSavePreferences}>
                                {t('consent.savePreferences')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CookieConsent;
